// src/components/SeoManager.jsx
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// ✅ SEO 설정 (모든 페이지)
const seoConfig = {
  "/": {
    title: "NamsanBro | Smart B2B & B2C Marketplace",
    description:
      "Discover NamsanBro — the global B2B & B2C marketplace for premium Korean products, home improvement, and lifestyle solutions.",
    keywords: "Korean products, B2B marketplace, B2C, home improvement, NamsanBro",
    image: "/assets/og-image-home.jpg" // ✅ 실제 경로 필요
  },
  "/products": {
    title: "All Products | NamsanBro",
    description:
      "Browse all available products on NamsanBro. Explore categories, compare quality, and find your next business or home essentials.",
    keywords: "products, B2B, Korean goods, wholesale",
    image: "/assets/og-image-products.jpg"
  },
  "/latest": {
    title: "Latest Arrivals | NamsanBro",
    description:
      "Stay updated with the latest arrivals on NamsanBro. Discover new trends and products added daily.",
    keywords: "new products, latest arrivals, B2B trends",
    image: "/assets/og-image-latest.jpg"
  },
  "/popular": {
    title: "Popular Products | NamsanBro",
    description:
      "See what’s trending now! Explore our most popular and best-selling products on NamsanBro.",
    keywords: "popular products, best sellers, trending",
    image: "/assets/og-image-popular.jpg"
  },
  "/recommended": {
    title: "Recommended Picks | NamsanBro",
    description:
      "Tailored just for you — discover recommended products on NamsanBro that fit your business and lifestyle.",
    keywords: "recommended products, personalized, B2B recommendations",
    image: "/assets/og-image-recommended.jpg"
  },
  "/cart": {
    title: "Your Cart | NamsanBro",
    description:
      "Review and manage your selected items before checkout. NamsanBro — simple, fast, and secure shopping.",
    keywords: "shopping cart, checkout, B2C",
    image: "/assets/og-image-cart.jpg"
  },
  "/profile": {
    title: "My Profile | NamsanBro",
    description:
      "Manage your account, orders, and personal information on your NamsanBro profile page.",
    keywords: "user profile, account settings",
    image: "/assets/og-image-profile.jpg"
  },
  "/buyer-orders": {
    title: "My Orders | NamsanBro",
    description:
      "Track your recent orders and view purchase details securely through NamsanBro’s order management.",
    keywords: "order history, purchase tracking",
    image: "/assets/og-image-orders.jpg"
  },
  "/become-seller": {
    title: "Become a Seller | NamsanBro",
    description:
      "Join NamsanBro’s global seller network. Start listing your products and grow your business globally.",
    keywords: "sell on NamsanBro, become seller, B2B seller",
    image: "/assets/og-image-seller.jpg"
  },
  "/seller/products": {
    title: "Seller Product Management | NamsanBro",
    description:
      "Easily manage, edit, and upload your products using NamsanBro’s powerful seller dashboard.",
    keywords: "seller dashboard, product management",
    image: "/assets/og-image-seller-products.jpg"
  },
  "/seller/orders": {
    title: "Order Management | Seller Dashboard",
    description:
      "Monitor and manage customer orders efficiently on NamsanBro Seller Dashboard.",
    keywords: "seller orders, order management",
    image: "/assets/og-image-seller-orders.jpg"
  },
  "/seller/analytics": {
    title: "Sales Analytics | NamsanBro",
    description:
      "Gain insights into your business performance and growth trends with real-time analytics tools.",
    keywords: "sales analytics, business insights",
    image: "/assets/og-image-analytics.jpg"
  },
  "/about-us": {
    title: "About Us | NamsanBro",
    description:
      "Learn more about NamsanBro’s mission to connect Korea with the global market through innovation and trust.",
    keywords: "about NamsanBro, company mission",
    image: "/assets/og-image-about.jpg"
  },
  "/privacy": {
    title: "Privacy Policy | NamsanBro",
    description:
      "We value your privacy. Read about how NamsanBro collects, uses, and protects your personal data.",
    keywords: "privacy policy, data protection",
    image: "/assets/og-image-privacy.jpg"
  },
  "/terms": {
    title: "Terms of Service | NamsanBro",
    description:
      "Review the terms and conditions for using NamsanBro’s services responsibly and safely.",
    keywords: "terms of service, user agreement",
    image: "/assets/og-image-terms.jpg"
  },
  "/disclaimer": {
    title: "Disclaimer | NamsanBro",
    description:
      "Understand our disclaimers and usage limitations. NamsanBro aims for transparency and trust.",
    keywords: "disclaimer, legal notice",
    image: "/assets/og-image-disclaimer.jpg"
  },
  "/seller-inquiry": {
    title: "Contact Sales | NamsanBro",
    description:
      "Interested in selling or partnership opportunities? Contact our business support team today.",
    keywords: "contact seller support, B2B inquiry",
    image: "/assets/og-image-contact.jpg"
  },
  // 기본값
  default: {
    title: "NamsanBro | Global Marketplace for Smart Buyers & Sellers",
    description:
      "Shop or sell with confidence on NamsanBro — a trusted marketplace connecting Korea and the world.",
    keywords: "NamsanBro, Korean marketplace, B2B, B2C",
    image: "/assets/og-image-default.jpg"
  }
};

const SeoManager = () => {
  const location = useLocation();
  const path = location.pathname;

  useEffect(() => {
    const seo = seoConfig[path] || seoConfig.default;
    const url = `https://namsan-bro.web.app${path}`;
    const imageUrl = `${window.location.origin}${seo.image}`;

    // ✅ 1. Title
    document.title = seo.title;

    // ✅ 2. 기존 메타/링크 제거 (중복 방지)
    const tagsToRemove = [
      'meta[name="description"]',
      'meta[name="keywords"]',
      'meta[property="og:title"]',
      'meta[property="og:description"]',
      'meta[property="og:image"]',
      'meta[property="og:url"]',
      'meta[name="twitter:card"]',
      'meta[name="twitter:title"]',
      'meta[name="twitter:description"]',
      'meta[name="twitter:image"]',
      'link[rel="canonical"]',
      'link[rel="icon"]'
    ];
    tagsToRemove.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => el.remove());
    });

    // ✅ 3. Charset & Viewport (일반적으론 index.html에 있지만 SPA 동적 관리용)
    let charset = document.querySelector('meta[charset]');
    if (!charset) {
      charset = document.createElement('meta');
      charset.setAttribute('charset', 'UTF-8');
      document.head.appendChild(charset);
    }

    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.name = 'viewport';
      viewport.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(viewport);
    }

    // ✅ 4. Description & Keywords
    const createMeta = (name, content, attr = 'name') => {
      const meta = document.createElement('meta');
      meta.setAttribute(attr, name);
      meta.content = content;
      document.head.appendChild(meta);
    };

    createMeta('description', seo.description);
    createMeta('keywords', seo.keywords);

    // ✅ 5. Open Graph
    createMeta('og:title', seo.title, 'property');
    createMeta('og:description', seo.description, 'property');
    createMeta('og:image', imageUrl, 'property');
    createMeta('og:url', url, 'property');
    createMeta('og:type', 'website', 'property');

    // ✅ 6. Twitter Card
    createMeta('twitter:card', 'summary_large_image');
    createMeta('twitter:title', seo.title);
    createMeta('twitter:description', seo.description);
    createMeta('twitter:image', imageUrl);

    // ✅ 7. Canonical URL
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = url;
    document.head.appendChild(canonical);

    // ✅ 8. Favicon
    const icon = document.createElement('link');
    icon.rel = 'icon';
    icon.href = '/favicon.ico';
    document.head.appendChild(icon);

  }, [path]);

  return null;
};

export default SeoManager;